import { useMemo, useState } from "react";
import { Icon } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CustomDatePicker } from "ui-components/CustomInput";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getPackagingMaterialList } from "services/data.internal.service";
import { deactivatePackagingMaterial } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { activatePackagingMaterial} from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getPackagingMaterialStatusCount } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";
//new one
import { getBillingServiceList } from "services/data.billing.service";
import { deactivateBillingService } from "services/data.billing.service";
import { activateBillingService} from "services/data.billing.service";
import { getBillingServiceStatusCount } from "services/data.billing.service";

dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MM-YYYY",
  "DD MMM YYYY",
  "DD-MMM-YYYY",
  "YYYY/MM/DD",
];

// Invoice date may come back from the API in various formats (with or without a time part)
const parseDate = (value) => {
  if (!value) return null;
  // e.g. "01 / 08 / 2026" -> "01/08/2026"
  const str = String(value).trim().replace(/\s*([/-])\s*/g, "$1");
  const datePart = str.split(/[T\s](?=\d{1,2}:)/)[0];
  const parsed = dayjs(datePart, DATE_FORMATS, true);
  if (parsed.isValid()) return parsed;
  const fallback = dayjs(str);
  return fallback.isValid() ? fallback : null;
};

function BillingServiceList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["billing-service-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Billing Service"]) &&
      permissions["Billing Service"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Billing Service"]) &&
      permissions["Billing Service"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Billing Service"]) &&
      permissions["Billing Service"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["billing-service-list", { customerCode, active }],
    queryFn: () => {
    console.log("customerCode passed to getBillingServiceList:", customerCode);
    console.log("customerCode passed to active:", active);
    return getBillingServiceList({ customerCode, active });
  },
   enabled: !!customerCode,
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["billing-service-count", { customerCode, active }],
    queryFn: () => getBillingServiceStatusCount({ customerCode, active }),
     enabled: !!customerCode,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateBillingService, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activateBillingService, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/billing/add-new-billing-service");

  const onEdit = (req) => {
    console.log("req before navigate:", req);
    navigate("/billing/edit-billing-service", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    console.log(req);
    setDialog(
      <MessageDialog
        title={`Deactivate BillServiceID :${req.billServiceId}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate BillServiceID :${req.billServiceId}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "BillServiceId", accessorKey: "billServiceId" },
    { header: "Category", accessorKey: "category" },
    { header: "Criteria", accessorKey: "criteria" },
    { header: "InvoiceNumber", accessorKey: "invoiceNumber" },
    { header: "InvoiceDate", accessorKey: "invoiceDate" },
    //{ header: "ContainerNo", accessorKey: "containerNo" },
    { header: "ActualQty", accessorKey: "actualQty" },
    
    { header: "Description", accessorKey: "remark" },
    { header: "Created By", accessorKey: "createdBy" },
    { header: "Created On", accessorKey: "createdOn" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { billServiceId } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit billing service"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ customerCode,billServiceId });
                }}
              />
              <DelButton
                title="deactivate billing service"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ customerCode,billServiceId });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate billing service"
              disabled={!editPermission}
              onClick={() => onActive({ customerCode,billServiceId })}
            />
          );
        }
      },
    },
  ];
  const filteredData = useMemo(() => {
    if (!startDate && !endDate) return data;
    return data.filter((x) => {
      const parsed = parseDate(x.invoiceDate);
      if (!parsed) return false;
      const date = parsed.format("YYYY-MM-DD");
      return (!startDate || date >= startDate) && (!endDate || date <= endDate);
    });
  }, [data, startDate, endDate]);

  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          margin="10px"
        >
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;billing&nbsp; service
          </MDButton>
          <MDBox display="flex" gap={2} sx={{ "& .MuiTextField-root": { mb: 0, width: 200 } }}>
            <CustomDatePicker
              field={{ label: "Start Date", name: "startDate", value: startDate, onChange: setStartDate }}
            />
            <CustomDatePicker
              field={{ label: "End Date", name: "endDate", value: endDate, onChange: setEndDate }}
            />
          </MDBox>
        </MDBox>
        <BasicTable columns={columns} data={filteredData} />
      </TableCard>
    </ListLayout>
  );
}

export default BillingServiceList;
